import { db } from '@/models/db';
import type { DeltaPacketRecord } from '@/models/hydrate.ts';
import { AppraisalOfferV2 } from '@/models/registry.ts';
import type { BaseStore } from '@/models/stores/base.ts';
import { type AppraisalOfferV2 as AppraisalOfferV2Type } from '@/models/types.ts';
import { makeAutoObservable, observable, type ObservableMap } from 'mobx';

class AppraisalOfferStoreV2
  implements BaseStore<AppraisalOfferV2, AppraisalOfferV2Type>
{
  store: ObservableMap<string, AppraisalOfferV2> = observable.map();

  // appraisal_v2_id -> array of appraisal offers
  storeIndexedByForeignKey: ObservableMap<string, AppraisalOfferV2[]> =
    observable.map();

  constructor() {
    makeAutoObservable(
      this,
      {
        store: observable.shallow,
        storeIndexedByForeignKey: observable.shallow,
      },
      { autoBind: true },
    );
  }

  private setAppraisalOfferField = <K extends keyof AppraisalOfferV2Type>(
    entry: AppraisalOfferV2,
    key: K,
    value: AppraisalOfferV2Type[K],
  ) => {
    (entry as Pick<AppraisalOfferV2, K>)[key] = value as AppraisalOfferV2[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AppraisalOfferV2Type>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.appraisal_offers_v2.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.appraisal_offers_v2.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.appraisal_offers_v2.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.appraisal_offers_v2.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<AppraisalOfferV2Type>;
  }) {
    const existingAppraisalOffer = this.getById(delta.record_id);
    if (!existingAppraisalOffer) return;

    let changed = false;

    const entries = Object.entries(delta.data) as [
      keyof AppraisalOfferV2Type,
      AppraisalOfferV2Type[keyof AppraisalOfferV2Type],
    ][];

    for (const [key, newValue] of entries) {
      if (newValue === undefined || existingAppraisalOffer[key] === newValue) {
        continue;
      }

      this.setAppraisalOfferField(existingAppraisalOffer, key, newValue);
      changed = true;
    }

    if (!changed) return;
    this.save(existingAppraisalOffer);
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<AppraisalOfferV2Type>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AppraisalOfferV2Type>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete') {
      this.remove(delta.record_id);
      return;
    }

    if (hydrateIndexedDB) {
      db.appraisal_offers_v2.put(delta.data, delta.record_id);
    }

    this.upsert(delta.data);
  }

  private getOrCreateCollection(
    map: Map<string, AppraisalOfferV2[]>,
    key: string,
  ): AppraisalOfferV2[] {
    let arr = map.get(key);
    if (!arr) {
      arr = observable.array<AppraisalOfferV2>([], { deep: false });
      map.set(key, arr);
    }
    return arr;
  }

  addToIndexes(offer: AppraisalOfferV2) {
    this.getOrCreateCollection(
      this.storeIndexedByForeignKey,
      offer.appraisal_v2_id, // Changed from appraisal_id
    ).push(offer);
  }

  removeFromIndexes(offer: AppraisalOfferV2) {
    const removeFrom = (arr: AppraisalOfferV2[]) => {
      const i = arr.findIndex((o) => o.id === offer.id);
      if (i >= 0) arr.splice(i, 1);
    };

    removeFrom(
      this.getOrCreateCollection(
        this.storeIndexedByForeignKey,
        offer.appraisal_v2_id, // Changed from appraisal_id
      ),
    );
  }

  get all() {
    return Array.from(this.store.values());
  }

  getById(id: string): AppraisalOfferV2 | undefined {
    return this.store.get(id);
  }

  add(input: AppraisalOfferV2Type): void {
    const appraisalOffer = new AppraisalOfferV2(input);

    this.store.set(appraisalOffer.id, appraisalOffer);
    this.addToIndexes(appraisalOffer);
  }

  upsert(appraisalOffer: AppraisalOfferV2Type) {
    const idToUse = appraisalOffer.id; // No String() casting needed for UUID
    const existingOffer = this.getById(idToUse);
    if (existingOffer) {
      this.replace({ id: idToUse, appraisalOffer });
    } else {
      this.add(appraisalOffer);
    }
  }

  save(offer: AppraisalOfferV2) {
    this.store.set(offer.id, offer);

    const arr = this.getOrCreateCollection(
      this.storeIndexedByForeignKey,
      offer.appraisal_v2_id, // Changed from appraisal_id
    );
    const idx = arr.findIndex((o) => o.id === offer.id);
    if (idx >= 0) arr.splice(idx, 1, offer);
  }

  replace({
    id,
    appraisalOffer,
  }: {
    id: string;
    appraisalOffer: AppraisalOfferV2Type;
  }) {
    const existingOffer = this.getById(id);
    if (!existingOffer) return;

    existingOffer.tracking_id = appraisalOffer.tracking_id;
    existingOffer.offer_type = appraisalOffer.offer_type;
    existingOffer.offer_amount_dollars = appraisalOffer.offer_amount_dollars;
    existingOffer.appraisal_v2_id = appraisalOffer.appraisal_v2_id;
    existingOffer.status = appraisalOffer.status;
    existingOffer.expires_at = appraisalOffer.expires_at;
    existingOffer.created_at = appraisalOffer.created_at;
    existingOffer.updated_at = appraisalOffer.updated_at;

    this.save(existingOffer);
  }

  remove(id: AppraisalOfferV2Type['id']) {
    const idToUse = id; // No String() casting needed for UUID
    const offer = this.getById(idToUse);
    if (!offer) return;
    this.store.delete(idToUse);
    this.removeFromIndexes(offer);

    db.appraisal_offers_v2.delete(idToUse);
  }
}

export const appraisalOfferStoreV2 = new AppraisalOfferStoreV2();
